import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export function useLeavePageGuard(shouldBlock: boolean) {
  const location = useLocation();
  const navigate = useNavigate();

  const lastConfirmedPath = useRef(location.pathname);
  const pendingPath = useRef<string | null>(null);
  const isNavigating = useRef(false);

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle browser refresh / tab close
  useEffect(() => {
    if (!shouldBlock) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue =
        'You have unsaved changes. Are you sure you want to leave?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldBlock]);

  // Detect location changes
  useEffect(() => {
    if (!shouldBlock) {
      lastConfirmedPath.current = location.pathname;
      return;
    }

    if (isNavigating.current) {
      // navigation confirmed via modal
      isNavigating.current = false;
      lastConfirmedPath.current = location.pathname;
      return;
    }

    if (location.pathname !== lastConfirmedPath.current) {
      pendingPath.current = location.pathname;
      isNavigating.current = true;
      navigate(lastConfirmedPath.current, { replace: true });

      // Defer modal open to next tick (after rollback)
      setTimeout(() => {
        setIsModalOpen(true);
      }, 0);
    }
  }, [location, navigate, shouldBlock]);

  const confirmLeave = () => {
    if (pendingPath.current) {
      isNavigating.current = true;
      navigate(pendingPath.current);
    }
    pendingPath.current = null;
    setIsModalOpen(false);
  };

  const cancelLeave = () => {
    pendingPath.current = null;
    setIsModalOpen(false);
  };

  return { isModalOpen, confirmLeave, cancelLeave };
}
